<template #videoPlayerTemplate>
  <c11n-proto-b5-video-player source="wistia" button-centered>
    <img alt="" src="" data-wistia-id="">
    <button aria-label="">
      <c11n-web-v1-icon name="play"></c11n-web-v1-icon>
    </button>
  </c11n-proto-b5-video-player>
</template>

<div class="student-learning-content" #lessonContentContainer>
  <div class="content-first">
    <div class="lesson-header-row">
      <nav class="lesson-breadcrumbs" [attr.aria-label]="'studentLearningQuiz.breadcrumb_label' | translate">
        <button #backToLibraryBtn
          type="button"
          class="lesson-breadcrumb-back"
          (click)="goBack()"
          c11n-link
          [variant]="'secondary-independent'">
          {{ 'studentLearningQuiz.back_to_learning_library' | translate }}
        </button>
        <ng-container *ngIf="lessonContent?.title">
          <span class="lesson-breadcrumb-separator" aria-hidden="true">/</span>
          <span class="lesson-breadcrumb-current">{{ lessonContent?.title }}</span>
        </ng-container>
      </nav>
      
      <div *ngIf="lessonContent"
        class="lesson-progress-bar"
        role="progressbar"
        [attr.aria-label]="'studentLearningQuiz.lesson_progress_label' | translate"
        aria-valuemin="0"
        [attr.aria-valuenow]="currentPageNumber"
        [attr.aria-valuemax]="lessonContent.totalPages"
        [attr.aria-valuetext]="'studentLearningQuiz.lesson_progress_aria' | translate: { current: currentPageNumber, total: lessonContent.totalPages }">
        <span
          *ngFor="let page of lessonContent.pages; let i = index"
          class="lesson-progress-segment"
          [class.lesson-progress-segment--completed]="i + 1 <= currentPageNumber"
          aria-hidden="true">
        </span>
      </div>
      <span
        class="visually-hidden"
        aria-live="polite"
        aria-atomic="true">{{ pageAnnouncement }}</span>
    </div>

    <div *ngIf="lessonContent && currentPage">
      <h2 class="c11n-text-xl-headline" id="lesson-title">
        {{ currentPage.pageTitle }}
      </h2>

      <div class="content-body">
        <div *ngIf="currentPage.content.paragraph" class="paragraph-content">
          <div [innerHTML]="safeHtmlContent"></div>
        </div>

        <!-- Images are embedded in the HTML content above, not separate -->

        <div *ngFor="let section of currentPage.content.sections" class="content-section">
          <h3 *ngIf="section.heading">{{ section.heading }}</h3>
          <p *ngIf="section.description">{{ section.description }}</p>
          <ul *ngIf="section.bulletPoints">
            <li *ngFor="let point of section.bulletPoints">{{ point }}</li>
          </ul>
        </div>

        <div *ngIf="currentPage.content.additionalParagraph" class="paragraph-content">
          <div class="d-flex flex-column gap-3" [innerHTML]="additionalParagraphContent"></div>
        </div>
      </div>
    </div>
    <div *ngIf="currentPage && currentPage.content && currentPage.content.activities" class="activities-section">
      <div *ngFor="let activity of currentPage.content.activities" class="activity-card">
        <h5>{{ activity.title }}</h5>
        <p>{{ activity.description }}</p>
      </div>
    </div>
  </div>
  <div class="content-second">
    <div *ngIf="currentPage && currentPage.content && currentPage.content.keyTerms" class="key-terms-section">
      <div *ngIf="!isLargeScreen" class="key-terms-accordion-collapsed">
        <div class="key-terms-header">
          <div class="heading-icon">
            <img src="./assets/icons/Learning-alt.svg" alt="" onerror="this.style.display='none'" />
          </div>
          <h3>{{ currentPage.content.keyTerms.heading }}</h3>
        </div>
        <div class="accordion-toggle">
          <button type="button" [ngClass]="{'expanded': !showKeyTermsAccordion}"
            [attr.aria-label]="showKeyTermsAccordion ? 'Key Terms collapse/expand expanded' : 'Key Terms collapse/expand collapsed'"
            (click)="toggleKeyTermsAccordion()">
            <img src="./assets/icons/arrow-down.svg" alt="">
          </button>
        </div>
      </div>

      <div *ngIf="isLargeScreen" class="key-terms-header-static">
        <div class="heading-icon">
          <img src="./assets/icons/Learning-alt.svg" alt="" onerror="this.style.display='none'" />
        </div>
        <h3>{{ currentPage.content.keyTerms.heading }}</h3>
      </div>

      <div class="key-terms-content" *ngIf="isLargeScreen || showKeyTermsAccordion">
        <p class="key-terms-description">
          {{ currentPage.content.keyTerms.description }}
        </p>
        <ul role="list" class="c11n-list c11n-text-md key-terms-list">
          <li *ngFor="let term of currentPage.content.keyTerms.terms">
            <strong class="key-term">{{ term.term }}:</strong>
            <span class="key-term-def">{{ term.definition }}</span>
          </li>
        </ul>
      </div>

      <div *ngIf="!isLargeScreen" class="pagination-footer-accordion">
        <div class="pagination-buttons">
          <c11n-button
            [class.button-disabled]="currentPageNumber === 1 || (userRole === 'teacher' && lessonContent?.totalPages === 1)"
            [disabled]="currentPageNumber === 1 || (userRole === 'teacher' && lessonContent?.totalPages === 1)"
            [attr.aria-disabled]="currentPageNumber === 1 || (userRole === 'teacher' && lessonContent?.totalPages === 1)"
            [ariaLabelText]="'studentLearningQuiz.previous_page_button' | translate" [iconName]="'link-left'"
            [iconPosition]="'leading'" [labelText]="'studentLearningQuiz.previous_button' | translate"
            [variant]="'secondary'" [size]="isXsScreen ? 'small' : 'medium'"
            (clickEvent)="previousPage()"></c11n-button>

          <c11n-button [class.button-disabled]="isNextButtonDisabled()" [disabled]="isNextButtonDisabled()"
            [attr.aria-disabled]="isNextButtonDisabled()"
            [ariaLabelText]="'studentLearningQuiz.next_page_button' | translate" [iconName]="'link-right'"
            [iconPosition]="'trailing'" [labelText]="'studentLearningQuiz.next_button' | translate"
            [variant]="'primary'" [size]="isXsScreen ? 'small' : 'medium'" (clickEvent)="nextPage()"></c11n-button>
        </div>
        <div class="pagination-left">
          <span class="page-indicator">
            {{ 'studentLearningQuiz.page_text' | translate }} {{ currentPageNumber }} {{ 'studentLearningQuiz.of_text'
            | translate }} {{ lessonContent?.totalPages }}
          </span>
        </div>
      </div>
    </div>
    <div *ngIf="isLargeScreen || !currentPage || !currentPage.content || !currentPage.content.keyTerms"
      class="pagination-footer" [class.no-key-terms]="!currentPage?.content?.keyTerms">
      <div class="pagination-buttons">
        <c11n-button
          [class.button-disabled]="currentPageNumber === 1 || (userRole === 'teacher' && lessonContent?.totalPages === 1)"
          [disabled]="currentPageNumber === 1 || (userRole === 'teacher' && lessonContent?.totalPages === 1)"
          [attr.aria-disabled]="currentPageNumber === 1 || (userRole === 'teacher' && lessonContent?.totalPages === 1)"
          [ariaLabelText]="'studentLearningQuiz.previous_page_button' | translate" [iconName]="'link-left'"
          [iconPosition]="'leading'" [labelText]="'studentLearningQuiz.previous_button' | translate"
          [variant]="'secondary'" [size]="isXsScreen ? 'small' : 'medium'" (clickEvent)="previousPage()"></c11n-button>

        <c11n-button [class.button-disabled]="isNextButtonDisabled()" [disabled]="isNextButtonDisabled()"
          [attr.aria-disabled]="isNextButtonDisabled()"
          [ariaLabelText]="'studentLearningQuiz.next_page_button' | translate" [iconName]="'link-right'"
          [iconPosition]="'trailing'" [labelText]="'studentLearningQuiz.next_button' | translate" [variant]="'primary'"
          [size]="isXsScreen ? 'small' : 'medium'" (clickEvent)="nextPage()"></c11n-button>
      </div>
      <div class="pagination-left">
        <span class="page-indicator">
          {{ 'studentLearningQuiz.page_text' | translate }} {{ currentPageNumber }} {{ 'studentLearningQuiz.of_text' |
          translate }} {{ lessonContent?.totalPages }}
        </span>
      </div>
    </div>
  </div>
</div>